import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Crown } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AppShell } from "@/components/AppShell";
import { PlanCard } from "@/components/PlanCard";
import { PlanActions } from "@/components/PlanActions";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";

const RequestRow = ({ req, plan, onSelect, busy }) => (
  <div
    className="flex items-start gap-4 p-4 rounded-lg border border-white/10 bg-[#15151A] hover:border-[#D4AF37]/40 transition-colors"
    data-testid="join-request-row"
  >
    <Avatar className="w-16 h-16 border-2 border-[#D4AF37]/40 shrink-0">
      <AvatarImage src={req.user.photo} alt={req.user.name} className="object-cover" />
      <AvatarFallback className="bg-[#1A1A24] text-[#D4AF37] font-serif text-xl">
        {req.user.name?.[0] || "?"}
      </AvatarFallback>
    </Avatar>
    <div className="flex-1 min-w-0">
      <p className="text-base text-white font-semibold" data-testid="join-request-name">
        {req.user.name}
      </p>
      <p className="text-xs uppercase tracking-wider text-[#D4AF37]/80 mt-0.5">
        {[req.user.age && `${req.user.age}`, req.user.gender, req.user.neighborhood]
          .filter(Boolean)
          .join(" · ")}
      </p>
      {req.user.bio && (
        <p className="text-sm text-[#A0A0A0] mt-2 leading-relaxed line-clamp-3">{req.user.bio}</p>
      )}
    </div>
    <div className="shrink-0">
      {plan.status === "open" ? (
        <Button
          size="sm"
          disabled={busy}
          data-testid="select-joinee-button"
          onClick={() => onSelect(plan.plan_id, req.user.user_id)}
          className="bg-[#D4AF37] text-[#0B0B0F] hover:bg-[#F1D570] font-semibold rounded-md transition-colors"
        >
          <Crown className="w-3.5 h-3.5 mr-1.5" strokeWidth={2} />
          Select
        </Button>
      ) : req.status === "selected" ? (
        <Badge className="bg-[#D4AF37] text-[#0B0B0F] border-transparent" data-testid="request-selected-badge">
          Selected
        </Badge>
      ) : null}
    </div>
  </div>
);

export default function MyPlans() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api
      .get("/plans/my")
      .then((r) => setData(r.data))
      .catch(() => {
        setData({ created: [], joined: [] });
        toast.error("Could not load your plans");
      });
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const selectJoinee = async (planId, joineeUserId) => {
    setBusy(true);
    try {
      await api.post(`/plans/${planId}/select`, { joinee_user_id: joineeUserId });
      toast.success("Joinee selected — your table is set");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not select joinee");
    } finally {
      setBusy(false);
    }
  };

  const empty = (text) => (
    <div className="text-center py-16 border border-dashed border-[#D4AF37]/25 rounded-xl" data-testid="my-plans-empty">
      <p className="text-sm text-[#A0A0A0]">{text}</p>
    </div>
  );

  return (
    <AppShell>
      <div data-testid="my-plans-page">
        <h1 className="font-serif text-3xl font-bold tracking-tight text-white mb-1">My Plans</h1>
        <p className="text-sm text-[#A0A0A0] mb-8">Tables you sponsor, and tables you've asked to join.</p>

        <Tabs defaultValue="created">
          <TabsList className="w-full bg-[#15151A] border border-[#D4AF37]/20 mb-6">
            <TabsTrigger
              value="created"
              data-testid="my-plans-created-tab"
              className="flex-1 data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0B0B0F] text-[#A0A0A0]"
            >
              Created
            </TabsTrigger>
            <TabsTrigger
              value="joined"
              data-testid="my-plans-joined-tab"
              className="flex-1 data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0B0B0F] text-[#A0A0A0]"
            >
              Joined
            </TabsTrigger>
          </TabsList>

          {data === null ? (
            <div className="space-y-5">
              {[0, 1].map((i) => (
                <Skeleton key={i} className="h-56 w-full rounded-xl bg-[#15151A]" />
              ))}
            </div>
          ) : (
            <>
              <TabsContent value="created" className="space-y-5 mt-0" data-testid="created-plans-list">
                {data.created.length === 0
                  ? empty("You haven't sponsored a table yet.")
                  : data.created.map((plan, i) => (
                      <PlanCard key={plan.plan_id} plan={plan} index={i} currentUserId={user?.user_id}>
                        {plan.status === "open" && (
                          <div className="mt-4 pt-4 border-t border-[#D4AF37]/15">
                            <p className="text-xs uppercase tracking-widest text-[#A0A0A0] mb-1">
                              Join Requests ({plan.requests.length})
                            </p>
                            {plan.requests.length === 0 ? (
                              <p className="text-xs text-[#5a5a66] italic py-2" data-testid="no-requests-label">
                                No requests yet — the circle is watching.
                              </p>
                            ) : (
                              <div className="space-y-3">
                                {plan.requests.map((req) => (
                                  <RequestRow
                                    key={req.request_id}
                                    req={req}
                                    plan={plan}
                                    onSelect={selectJoinee}
                                    busy={busy}
                                  />
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                        {plan.selected_joinee && (
                          <div
                            className="mt-4 pt-4 border-t border-[#D4AF37]/15 flex items-center gap-3"
                            data-testid="selected-joinee-row"
                          >
                            <Avatar className="w-9 h-9 border border-[#D4AF37]/40">
                              <AvatarImage src={plan.selected_joinee.photo} alt={plan.selected_joinee.name} />
                              <AvatarFallback className="bg-[#1A1A24] text-[#D4AF37] text-xs">
                                {plan.selected_joinee.name?.[0] || "?"}
                              </AvatarFallback>
                            </Avatar>
                            <div>
                              <p className="text-sm text-white font-medium">{plan.selected_joinee.name}</p>
                              <p className="text-[10px] uppercase tracking-widest text-[#D4AF37]">Guest of Honour</p>
                            </div>
                          </div>
                        )}
                        <PlanActions plan={plan} role="planner" onChanged={load} />
                      </PlanCard>
                    ))}
              </TabsContent>

              <TabsContent value="joined" className="space-y-5 mt-0" data-testid="joined-plans-list">
                {data.joined.length === 0
                  ? empty("You haven't requested to join a table yet.")
                  : data.joined.map((plan, i) => (
                      <PlanCard key={plan.plan_id} plan={plan} index={i} currentUserId={user?.user_id}>
                        {plan.my_request_status === "selected" && (
                          <PlanActions plan={plan} role="joinee" onChanged={load} />
                        )}
                      </PlanCard>
                    ))}
              </TabsContent>
            </>
          )}
        </Tabs>
      </div>
    </AppShell>
  );
}
